import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { TrashIcon, PencilIcon, UserGroupIcon, CurrencyDollarIcon, AcademicCapIcon, ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from '../../hooks/useTranslations';
import toast from 'react-hot-toast';
// import LanguageSwitcher from '../../components/LanguageSwitcher';


function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Загружаем фильтры из localStorage или используем значения по умолчанию
  const [filters, setFilters] = useState(() => {
    try {
      const savedFilters = localStorage.getItem('adminUsersFilters');
      return savedFilters ? JSON.parse(savedFilters) : {
        course: 'all',
        promotion: 'all',
        code: 'all',
        overdraft: 'all'
      };
    } catch {
      return {
        course: 'all',
        promotion: 'all',
        code: 'all',
        overdraft: 'all'
      };
    }
  });
  const [editingBalance, setEditingBalance] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [customCourse, setCustomCourse] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const { getAuthHeader, updateUserPromotion } = useAdminAuth();
  const { t } = useTranslations();

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('users.copied') || 'Скопировано!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(t('users.copyError') || 'Ошибка копирования');
    }
  };

  const COURSE_OPTIONS = [
    { value: 'NONE', label: t('users.noCourse') },
    { value: 'SILVER', label: t('users.silver') },
    { value: 'GOLD', label: t('users.gold') },
    { value: 'PLATINUM', label: t('users.platinum') },
    { value: 'CUSTOM', label: t('users.customOption') }
  ];

  const ITEMS_PER_PAGE = 100;

  function formatDate(dateString) {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getInitials(username, lastname) {
    if (!username && !lastname) return '?';
    return `${(username?.[0] || '').toUpperCase()}${(lastname?.[0] || '').toUpperCase()}`;
  }

  function getTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return t('users.justNow');
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}${t('users.minutesAgo')}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}${t('users.hoursAgo')}`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}${t('users.daysAgo')}`;
    return formatDate(dateString);
  }

  function getActivityStatus(lastActivity) {
    const now = new Date();
    const activityDate = new Date(lastActivity);
    const diffInMinutes = Math.floor((now - activityDate) / (1000 * 60));
    
    if (diffInMinutes < 5) return { color: 'bg-green-100 text-green-800', text: t('users.online') };
    if (diffInMinutes < 2880) return { color: 'bg-yellow-100 text-yellow-800', text: t('users.recently') };
    return { color: 'bg-gray-100 text-gray-800', text: t('users.inactive') };
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  // Сохраняем фильтры в localStorage при их изменении
  useEffect(() => {
    try {
      localStorage.setItem('adminUsersFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  useEffect(() => {
    let filtered = users.filter(user => {
      const searchLower = searchQuery.toLowerCase().trim();
      
      // Поиск
      const matchesSearch = !searchLower || (
        (user.user_id?.toString() || '').includes(searchLower) ||
        (user.username?.toLowerCase() || '').includes(searchLower) ||
        (user.lastname?.toLowerCase() || '').includes(searchLower) ||
        (`${user.username} ${user.lastname}`.toLowerCase() || '').includes(searchLower) ||
        (user.email?.toLowerCase() || '').includes(searchLower) ||
        (user.contact?.toLowerCase() || '').includes(searchLower) ||
        (user.course?.toLowerCase() || '').includes(searchLower) ||
        (user.code?.toLowerCase() || '').includes(searchLower)
      );
      
      // Фильтр по курсу
      const matchesCourse = filters.course === 'all' || 
        (filters.course === 'NONE' && (!user.course || user.course === 'NONE')) ||
        (filters.course !== 'NONE' && user.course === filters.course);
      
      // Фильтр по promotion
      const matchesPromotion = filters.promotion === 'all' ||
        (filters.promotion === 'true' && user.promotion === true) ||
        (filters.promotion === 'false' && user.promotion !== true);
      
      // Фильтр по промо-коду
      const matchesCode = filters.code === 'all' ||
        (filters.code === 'hasCode' && user.code && user.code.trim() !== '') ||
        (filters.code === 'noCode' && (!user.code || user.code.trim() === ''));
      
      // Фильтр по овердрафту
      const matchesOverdraft = filters.overdraft === 'all' ||
        (filters.overdraft === 'true' && user.overdraft === true) ||
        (filters.overdraft === 'false' && user.overdraft !== true);
      
      return matchesSearch && matchesCourse && matchesPromotion && matchesCode && matchesOverdraft;
    });
    
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, users, filters]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetch('https://api.maxprofitacademy.com/admin/user', {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      
      if (result.status === 200 && Array.isArray(result.data)) {
        const sortedUsers = result.data.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA;
        });
        setUsers(sortedUsers);
      } else {
        console.error('Invalid response format:', result);
        toast.error(t('users.invalidDataFormat'));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error(t('users.errorLoadingUsers'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('users.confirmDeleteUser'))) {
      return;
    }

    try {
      const response = await fetch(`https://api.maxprofitacademy.com/admin/user/delete/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      toast.success(t('users.userDeleted') || 'Пользователь удален');
      fetchUsers();
    } catch (err) {
      toast.error(t('users.errorDeletingUser'));
      console.error('Error deleting user:', err);
    }
  };

  const handleBalanceUpdate = async (userId) => {
    try {
      const response = await fetch(`https://api.maxprofitacademy.com/user/${userId}/balance`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balance: Number(newBalance) })
      });

      if (!response.ok) {
        throw new Error('Failed to update balance');
      }

      toast.success(t('users.balanceUpdated') || 'Баланс обновлен');
      setEditingBalance(null);
      setNewBalance('');
      fetchUsers();
    } catch (err) {
      toast.error(t('users.errorUpdatingBalance'));
      console.error('Error updating balance:', err);
    }
  };

  const handleCourseUpdate = async (userId, newCourse) => {
    try {
      const courseValue = newCourse === 'CUSTOM' ? customCourse : newCourse;
      
      const response = await fetch(`https://api.maxprofitacademy.com/user/${userId}/course`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ course: courseValue })
      });

      if (!response.ok) {
        throw new Error('Failed to update course');
      }

      toast.success(t('users.courseUpdated') || 'Курс обновлен');
      setEditingCourse(null);
      setCustomCourse('');
      setShowCustomInput(false);
      fetchUsers();
    } catch (err) {
      toast.error(t('users.errorUpdatingCourse'));
      console.error('Error updating course:', err);
    }
  };

  const handlePromotionToggle = async (userId, currentPromotion) => {
    try {
      const newPromotionValue = !currentPromotion;
      await updateUserPromotion(userId, newPromotionValue);
      toast.success(newPromotionValue ? (t('users.promotionActivated') || 'Скидка активирована') : (t('users.promotionDeactivated') || 'Скидка деактивирована'));
      fetchUsers();
    } catch (err) {
      toast.error(t('users.errorUpdatingPromotion'));
      console.error('Error updating promotion:', err);
    }
  };

  const handleActivateAdminCode = async (userId) => {
    if (!window.confirm(t('users.confirmActivateAdminCode') || 'Активировать админский код для этого пользователя?')) {
      return;
    }

    try {
      const response = await fetch(`https://api.maxprofitacademy.com/admin/user/${userId}/activate-code`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to activate admin code');
      }

      const result = await response.json();
      
      if (result.status === 200) {
        toast.success(t('users.adminCodeActivated') || 'Админ-код активирован');
        fetchUsers();
        setEditingUser(null);
      }
    } catch (err) {
      toast.error(t('users.errorActivatingAdminCode') || 'Ошибка при активации админского кода');
      console.error('Error activating admin code:', err);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
  };

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Управление пользователями</h2>
        <p className="text-gray-600 mt-1">Просмотр и управление пользователями платформы</p>
      </div> */}

      {/* Поиск и фильтры */}
      <div className="mb-6 space-y-4">
        <div className="flex justify-between items-center gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t('users.searchPlaceholder') || 'Поиск по ID, имени, email, телефону, курсу...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="text-sm text-gray-600">
            {t('users.showing') || 'Показано'}: <span className="font-semibold text-gray-900">{filteredUsers.length}</span> {t('users.of') || 'из'} {users.length}
          </div>
        </div>

        {/* Фильтры чипсами */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="space-y-3">
            {/* Курсы */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">{t('users.filterByCourse') || 'Курс'}:</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilters({...filters, course: 'all'})}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    filters.course === 'all' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('users.all') || 'Все'} ({users.length})
                </button>
                <button
                  onClick={() => setFilters({...filters, course: 'NONE'})}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    filters.course === 'NONE' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('users.noCourse')} ({users.filter(u => !u.course || u.course === 'NONE').length})
                </button>
                <button
                  onClick={() => setFilters({...filters, course: 'SILVER'})}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    filters.course === 'SILVER' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  SILVER ({users.filter(u => u.course === 'SILVER').length})
                </button>
                <button
                  onClick={() => setFilters({...filters, course: 'GOLD'})}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    filters.course === 'GOLD' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  GOLD ({users.filter(u => u.course === 'GOLD').length})
                </button>
                <button
                  onClick={() => setFilters({...filters, course: 'PLATINUM'})}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    filters.course === 'PLATINUM' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  PLATINUM ({users.filter(u => u.course === 'PLATINUM').length})
                </button>
              </div>
            </div>

            {/* Промо и коды */}
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-gray-600 mb-2 block">{t('users.promotion')}:</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilters({...filters, promotion: 'all'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.promotion === 'all' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.all') || 'Все'}
                  </button>
                  <button
                    onClick={() => setFilters({...filters, promotion: 'true'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.promotion === 'true' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.promotionActive')} ({users.filter(u => u.promotion === true).length})
                  </button>
                  <button
                    onClick={() => setFilters({...filters, promotion: 'false'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.promotion === 'false' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.promotionInactive')} ({users.filter(u => u.promotion !== true).length})
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-gray-600 mb-2 block">{t('users.promoCode')}:</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilters({...filters, code: 'all'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.code === 'all' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.all') || 'Все'}
                  </button>
                  <button
                    onClick={() => setFilters({...filters, code: 'hasCode'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.code === 'hasCode' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.withCode') || 'С кодом'} ({users.filter(u => u.code && u.code.trim() !== '').length})
                  </button>
                  <button
                    onClick={() => setFilters({...filters, code: 'noCode'})}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      filters.code === 'noCode' 
                        ? 'bg-primary text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('users.withoutCode') || 'Без кода'} ({users.filter(u => !u.code || u.code.trim() === '').length})
                  </button>
                </div>
              </div>
            </div>

            {/* Сброс фильтров */}
            {(filters.course !== 'all' || filters.promotion !== 'all' || filters.code !== 'all' || filters.overdraft !== 'all') && (
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={() => {
                    const defaultFilters = {
                      course: 'all',
                      promotion: 'all',
                      code: 'all',
                      overdraft: 'all'
                    };
                    setFilters(defaultFilters);
                    localStorage.setItem('adminUsersFilters', JSON.stringify(defaultFilters));
                  }}
                  className="text-xs text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  ✕ {t('users.resetFilters') || 'Сбросить фильтры'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.nameAndSurname')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.id')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                  {t('users.phone')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden xl:table-cell">
                  {t('users.password')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.balance')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.course')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                  {t('users.registrationDate')}
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.activity')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.promotion')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.code') || 'Код'}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {t('users.actions')}
                </th>
              </tr>
            </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {user.avatar ? (
                            <img
                              className="h-10 w-10 rounded-full ring-2 ring-gray-100"
                              src={user.avatar}
                              alt=""
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shadow-sm">
                              {getInitials(user.username, user.lastname)}
                            </div>
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="flex items-center group">
                            <div className="text-sm font-semibold text-gray-900">
                              {user.username} {user.lastname}
                            </div>
                            <button
                              onClick={() => copyToClipboard(`${user.username} ${user.lastname}`)}
                              className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                              title={t('users.copy')}
                            >
                              <ClipboardDocumentIcon className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="flex items-center mt-0.5 group">
                            <div className="text-xs text-gray-500">
                              {user.email}
                            </div>
                            <button
                              onClick={() => copyToClipboard(user.email)}
                              className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                              title={t('users.copy')}
                            >
                              <ClipboardDocumentIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-bold">{user.user_id}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap hidden lg:table-cell">
                      <div className="flex items-center group">
                        <div className="text-xs text-gray-900">{user.contact}</div>
                        <button
                          onClick={() => copyToClipboard(user.contact)}
                          className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                          title={t('users.copy')}
                        >
                          <ClipboardDocumentIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap hidden xl:table-cell">
                      <div className="flex items-center group">
                        <div className="text-xs text-gray-900">{user.password}</div>
                        <button
                          onClick={() => copyToClipboard(user.password)}
                          className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                          title={t('users.copy')}
                        >
                          <ClipboardDocumentIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <div className="text-xs text-gray-900">{user.balance}</div>
                        <button
                          onClick={() => {
                            setEditingBalance(user.user_id);
                            setNewBalance(user.balance);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                      </div>
                      {user.overdraft && (
                        <div className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-yellow-100 text-yellow-800 mt-0.5">
                          {t('users.overdraft')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <div className="text-xs text-gray-900">{user.course || t('users.noCourse')}</div>
                        <button
                          onClick={() => setEditingCourse(user.user_id)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-xs text-gray-900">
                        {user.created_at ? formatDate(user.created_at) : t('users.noDate')}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-900">
                          {user.last_activity ? getTimeAgo(user.last_activity) : t('users.noData')}
                        </div>
                        {user.last_activity && (
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium mt-0.5 ${getActivityStatus(user.last_activity).color}`}>
                            {getActivityStatus(user.last_activity).text}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() => handlePromotionToggle(user.user_id, user.promotion)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          user.promotion ? 'bg-primary' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={user.promotion}
                        title={t('users.promotionTooltip')}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            user.promotion ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {user.code ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {user.code}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">{t('users.noCode') || 'Нет'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-800"
                          title={t('users.edit') || 'Редактировать'}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.user_id)}
                          className="text-red-600 hover:text-red-800"
                          title={t('users.delete') || 'Удалить'}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      <div className="mt-3 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="text-xs text-gray-600">
          {t('users.showing')} {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} {t('users.of')} {filteredUsers.length} {t('users.users')}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t('users.back')}
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t('users.forward')}
          </button>
        </div>
      </div>

      {/* Модальное окно для баланса */}
      {editingBalance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{t('users.changeBalance')}</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="balance" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.newBalance')}
                </label>
                <input
                  type="number"
                  id="balance"
                  value={newBalance}
                  onChange={(e) => setNewBalance(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  required
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingBalance(null);
                    setNewBalance('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  {t('users.cancel')}
                </button>
                <button
                  onClick={() => handleBalanceUpdate(editingBalance)}
                  className="px-3 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90"
                >
                  {t('users.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для курса */}
      {editingCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{t('users.selectCourse')}</h3>
            <div className="space-y-4">
              {!showCustomInput ? (
                <div className="grid grid-cols-2 gap-3">
                  {COURSE_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        if (option.value === 'CUSTOM') {
                          setShowCustomInput(true);
                        } else {
                          handleCourseUpdate(editingCourse, option.value);
                        }
                      }}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="customCourse" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.enterCourseName')}
                    </label>
                    <input
                      type="text"
                      id="customCourse"
                      value={customCourse}
                      onChange={(e) => setCustomCourse(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                      placeholder={t('users.enterCourseNamePlaceholder')}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomCourse('');
                      }}
                      className="px-4 py-2 text-gray-700 hover:text-gray-900"
                    >
                      {t('users.back')}
                    </button>
                    <button
                      onClick={() => handleCourseUpdate(editingCourse, 'CUSTOM')}
                      disabled={!customCourse.trim()}
                      className="px-4 py-2 bg-primary text-black font-medium rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {t('users.save')}
                    </button>
                  </div>
                </div>
              )}
              {!showCustomInput && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCourse(null);
                      setCustomCourse('');
                      setShowCustomInput(false);
                    }}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    {t('users.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования пользователя */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {t('users.editUser') || 'Редактирование пользователя'}
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Основная информация */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.id')}</label>
                  <p className="text-sm font-semibold text-gray-900">{editingUser.user_id}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.registrationDate')}</label>
                  <p className="text-sm text-gray-900">{formatDate(editingUser.created_at)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.firstName') || 'Имя'}</label>
                  <p className="text-sm font-semibold text-gray-900">{editingUser.username}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.lastName') || 'Фамилия'}</label>
                  <p className="text-sm font-semibold text-gray-900">{editingUser.lastname}</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.email')}</label>
                <p className="text-sm text-gray-900">{editingUser.email}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('users.phone')}</label>
                <p className="text-sm text-gray-900">{editingUser.contact}</p>
              </div>

              {/* Управляемые параметры */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">{t('users.settings') || 'Настройки'}</h4>
                
                <div className="space-y-4">
                  {/* Баланс */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-900">{t('users.balance')}</label>
                      <p className="text-xs text-gray-600">{t('users.currentBalance') || 'Текущий баланс'}: ${editingUser.balance}</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingBalance(editingUser.user_id);
                        setNewBalance(editingUser.balance);
                        setEditingUser(null);
                      }}
                      className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90"
                    >
                      {t('users.change') || 'Изменить'}
                    </button>
                  </div>

                  {/* Курс */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-900">{t('users.course')}</label>
                      <p className="text-xs text-gray-600">{t('users.currentCourse') || 'Текущий курс'}: {editingUser.course || t('users.noCourse')}</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingCourse(editingUser.user_id);
                        setEditingUser(null);
                      }}
                      className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90"
                    >
                      {t('users.change') || 'Изменить'}
                    </button>
                  </div>

                  {/* Promotion */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-900">{t('users.promotion')}</label>
                      <p className="text-xs text-gray-600">{t('users.promotionDescription') || 'Скидка -50% на курсы'}</p>
                    </div>
                    <button
                      onClick={() => {
                        handlePromotionToggle(editingUser.user_id, editingUser.promotion);
                        setEditingUser(null);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editingUser.promotion ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editingUser.promotion ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Промо-код */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-900">{t('users.promoCode') || 'Промо-код'}</label>
                      <p className="text-xs text-gray-600">
                        {editingUser.code ? (
                          <span className="text-green-600 font-semibold">{editingUser.code}</span>
                        ) : (
                          t('users.noCodeActivated') || 'Промо-код не активирован'
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => handleActivateAdminCode(editingUser.user_id)}
                      className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90"
                    >
                      {t('users.activateStandardPrices') || 'Активировать обычные цены'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users; 